import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logoMark from '../assets/images/logo.jpg';
import { Reveal } from '../ui';
import PricingPlans from '../billing/PricingPlans';
import CheckoutModal from '../billing/CheckoutModal';
import EntitlementCards from '../billing/EntitlementCards';
import { fetchPackages, fetchEntitlements } from '../billing/checkout';
import '../billing/billing.css';

/* ==================================================================
   Pricing
   ------------------------------------------------------------------
   The standalone pricing screen. Readable signed out (so it can be
   linked from anywhere), but buying requires a recruiter session —
   the order endpoint authenticates from the JWT, so an anonymous
   visitor is sent to sign in rather than shown a checkout that would
   fail with a 401 halfway through.
   ================================================================== */

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

export default function Pricing({ onLogout }) {
  const navigate = useNavigate();
  const user = currentUser();
  const isRecruiter = user?.role === 'recruiter';

  const [packages, setPackages] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkout, setCheckout] = useState({ open: false, pkg: null });

  useEffect(() => {
    let cancelled = false;

    fetchPackages()
      .then((data) => { if (!cancelled) setPackages(data); })
      .catch(() => {
        if (!cancelled) setError('We could not load our packages just now. Please refresh the page.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Only a recruiter has entitlements; asking as anyone else would 403.
    if (isRecruiter) {
      fetchEntitlements()
        .then((data) => { if (!cancelled) setEntitlements(data); })
        .catch(() => { /* the summary simply does not render */ });
    }

    return () => { cancelled = true; };
  }, [isRecruiter]);

  const refreshEntitlements = useCallback(() => {
    if (!isRecruiter) return;
    fetchEntitlements().then(setEntitlements).catch(() => {});
  }, [isRecruiter]);

  const handleSelect = (pkg) => {
    if (!isRecruiter) {
      // Come back here after signing in rather than dumping them on a dashboard.
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    setCheckout({ open: true, pkg });
  };

  const paymentsDown = packages && packages.payments_enabled === false;

  return (
    <div className="bl-page">
      <nav className="fs-nav">
        <button className="fs-brand" onClick={() => navigate(isRecruiter ? '/recruiter/dashboard' : '/')}>
          <img src={logoMark} alt="" className="brand-mark" />
          <div>
            <div className="fs-brand-title">HYRSKILL</div>
            <div className="fs-brand-sub">{isRecruiter ? 'Recruiter Portal' : 'Pricing'}</div>
          </div>
        </button>
        <div className="fs-nav-actions">
          {isRecruiter ? (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/billing')}>
                Billing
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/dashboard')}>
                ← Dashboard
              </button>
              {onLogout && <button className="btn btn-secondary btn-sm" onClick={onLogout}>Sign Out</button>}
            </>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/login')}>
              Sign In
            </button>
          )}
        </div>
      </nav>

      <div className="bl-body ui-view">
        <Reveal as="header" className="bl-head">
          <h1>Hire from India’s top campuses</h1>
          <p>
            Two ways to hire, priced separately so you only pay for what you use.
            Unlock student profiles, post jobs, or do both.
          </p>
          <span className="bl-gst">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            All prices include GST · One-time payment · No subscription
          </span>
        </Reveal>

        {paymentsDown && (
          <div className="bl-banner bl-banner--warn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" />
            </svg>
            <div className="bl-banner-body">
              <strong>Payments are temporarily unavailable</strong>
              You can still browse packages — please check back shortly or contact support.
            </div>
          </div>
        )}

        {error && (
          <div className="bl-banner bl-banner--error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" />
            </svg>
            <div className="bl-banner-body">{error}</div>
          </div>
        )}

        {/* A recruiter who already holds credits sees what they have before
            being shown what else they could buy. */}
        {isRecruiter && entitlements && (
          <Reveal className="bl-section">
            <div className="bl-section-head">
              <div className="bl-section-title">
                <div>
                  <h2>Your current balance</h2>
                  <p className="bl-section-sub">Credits you can spend right now.</p>
                </div>
              </div>
            </div>
            <EntitlementCards
              entitlements={entitlements}
              onBuy={() => { /* already on the pricing page */ }}
              onFindStudents={() => navigate('/recruiter/find-students')}
              onPostJob={() => navigate('/recruiter/post-job')}
            />
          </Reveal>
        )}

        {loading && (
          <div className="bl-plans">
            {[0, 1, 2].map((i) => (
              <div key={i} className="ui-skel" style={{ height: 340, borderRadius: 16 }} />
            ))}
          </div>
        )}

        {!loading && packages && (
          <PricingPlans
            packages={packages}
            onSelect={handleSelect}
            disabled={paymentsDown}
          />
        )}
      </div>

      <CheckoutModal
        open={checkout.open}
        pkg={checkout.pkg}
        onClose={() => setCheckout({ open: false, pkg: null })}
        onSuccess={refreshEntitlements}
      />
    </div>
  );
}
